import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import API from '../services/api';
import { todayEastern } from '../dateUtils';
import SessionSummaryCard from './SessionSummaryCard';
import RevenueSummaryCard from './RevenueSummaryCard';
import CommunityCanvas from '../components/CommunityCanvas';
import BirthdayCardModal from '../components/BirthdayCardModal';
import TeamCard from './TeamCard';
import { ProfileEditModal, timeGreeting } from './ProfileCard';
import ConflictBanner from '../components/ConflictBanner';
import CollectionBanner from '../components/CollectionBanner';
import NewClientRequestBanner from '../components/NewClientRequestBanner';
import ProfileUpdateCompletedBanner from '../components/ProfileUpdateCompletedBanner';
import NewClientDetailModal from '../components/NewClientDetailModal';
import AnnouncementBanner from '../components/AnnouncementBanner';
import TherapistPerformanceCard from '../components/TherapistPerformanceCard';
import { isLimitedAdmin, hasArea } from '../services/auth';
import ScheduleCard from '../components/ScheduleCard';
import DashboardGrid from '../components/dashboard/DashboardGrid';
import useDashboardLayout from '../components/dashboard/useDashboardLayout';
import { ADMIN_WIDGETS } from '../components/dashboard/widgetRegistry';

/* ------------------------------- CelebrationCard ------------------------------- */
// Renders a card listing birthdays and work anniversaries for the current week.
function CelebrationCard({ items, currentUserId, onDrawCard }) {
  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #fce7f3 0%, #dbf4ff 100%)',
        border: '1px solid var(--border-color)',
        borderRadius: 16,
        padding: 16,
        marginBottom: 24,
        boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
      }}
    >
      <h3 style={{ marginTop: 0, marginBottom: 8 }}>🎉 Celebrations This Week</h3>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 12,
        }}
      >
        {items.map((it, idx) => (
          <div
            key={idx}
            style={{
              background: 'white',
              borderRadius: 12,
              padding: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              border: '1px solid var(--border-color)',
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                overflow: 'hidden',
                background: 'var(--surface-muted)',
                flexShrink: 0,
              }}
            >
              {it.photo ? (
                <img
                  src={it.photo}
                  alt={it.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--text-muted)',
                    fontWeight: 600,
                  }}
                >
                  {it.name
                    .split(' ')
                    .map((s) => s[0])
                    .join('')
                    .slice(0, 2)}
                </div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>
                {it.type === 'birthday'
                  ? `🎂 Happy Birthday, ${it.name}!`
                  : `🎉 Work Anniversary — ${it.name}`}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-strong)' }}>
                {it.type === 'birthday'
                  ? (it.date
                      ? it.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
                      : 'This week')
                  : (typeof it.years === 'number'
                      ? `${it.years === 0 ? 'First year!' : `${it.years} year${it.years === 1 ? '' : 's'}`} at the practice`
                      : 'This week')}
              </div>
              {it.userId && Number(it.userId) !== Number(currentUserId) && onDrawCard && (
                <button
                  onClick={() => onDrawCard(it)}
                  style={{
                    marginTop: 8, padding: '4px 12px', borderRadius: 6, fontSize: 12,
                    background: 'var(--primary-color)', color: '#fff', border: 'none',
                    cursor: 'pointer', fontWeight: 600,
                  }}
                >
                  🎨 Draw a card
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------- QuickActionsBar ------------------------------- */
// Renders a therapist-style quick actions bar for admin links.
function QuickActionsBar() {
  const actions = [
    { to: '/admin/daily-report', label: 'Daily Report', icon: '📅' },
    { to: '/admin/new-client-requests', label: 'New Client Requests', icon: '🗂️' },
    { to: '/admin/debt-records', label: 'Debt Records', icon: '💳' },
    { to: '/admin/messages', label: 'Messages', icon: '💬' },
    { to: '/admin/payroll', label: 'Payroll', icon: '💼' },
    { to: '/admin/invoice', label: 'Client Invoice', icon: '🧾' },
    { to: '/admin/users', label: 'Manage Users', icon: '👥' },
    { to: '/admin/canvas', label: 'Canvas', icon: '🎨' },
  ].filter((a) => {
    if (a.to === '/admin/payroll') return hasArea('payroll');
    if (a.to === '/admin/users') return hasArea('user_management');
    return true;
  });
  return (
    <div style={qaWrap}>
      {actions.map((a) => (
        <Link key={a.to} to={a.to} style={qaCard}>
          <div style={qaIcon}>{a.icon}</div>
          <div style={qaLabel}>{a.label}</div>
        </Link>
      ))}
    </div>
  );
}

function AdminDashboardPage() {
  const [therapists, setTherapists] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [cardTarget, setCardTarget] = useState(null);
  const [therapistProfilesMap, setTherapistProfilesMap] = useState({});
  const [scheduleSummaries, setScheduleSummaries] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [myProfile, setMyProfile] = useState(null);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [adminConflicts, setAdminConflicts] = useState([]);
  const [newCollections, setNewCollections] = useState([]);
  const [newClientBanner, setNewClientBanner] = useState([]);
  const [completedProfileUpdates, setCompletedProfileUpdates] = useState([]);
  const [newClientRequests, setNewClientRequests] = useState([]);
  const [newClientRequestsFailed, setNewClientRequestsFailed] = useState(false);
  const [requestDetailId, setRequestDetailId] = useState(null);
  const [managedCalendarId, setManagedCalendarId] = useState('');
  const limited = isLimitedAdmin();

  const visibleWidgets = useMemo(
    () => ADMIN_WIDGETS.filter((w) => !w.visibleWhen || w.visibleWhen({ limited, has: hasArea })),
    [limited]
  );
  const widgetIds = useMemo(() => visibleWidgets.map((w) => w.id), [visibleWidgets]);
  const { layout, move, hide, show, reset } = useDashboardLayout(widgetIds);

  useEffect(() => {
    let active = true;
    API.get('/admin/new-collections')
      .then((res) => { if (active && res.data) setNewCollections(res.data); })
      .catch(() => { /* non-fatal */ });
    return () => { active = false; };
  }, []);

  const dismissCollection = async (c) => {
    setNewCollections((prev) => prev.filter((x) => !(x.linkType === c.linkType && x.tokenId === c.tokenId)));
    try {
      await API.post('/admin/new-collections/dismiss', { linkType: c.linkType, tokenId: c.tokenId });
    } catch (_) { /* best-effort */ }
  };

  useEffect(() => {
    let active = true;
    API.get('/admin/new-client-requests/banner')
      .then((res) => { if (active && Array.isArray(res.data)) setNewClientBanner(res.data); })
      .catch(() => { /* non-fatal */ });
    return () => { active = false; };
  }, []);

  const dismissNewClientRequest = async (r) => {
    setNewClientBanner((prev) => prev.filter((x) => x.acuityId !== r.acuityId));
    try {
      await API.post('/admin/new-client-requests/dismiss', { acuityId: r.acuityId });
    } catch (_) { /* best-effort */ }
  };

  // Profile-update requests a therapist has completed since the office last looked.
  useEffect(() => {
    if (!hasArea('client_management')) return undefined;
    let active = true;
    API.get('/admin/profile-update-requests/completed-banner')
      .then((res) => { if (active && Array.isArray(res.data)) setCompletedProfileUpdates(res.data); })
      .catch(() => { /* non-fatal */ });
    return () => { active = false; };
  }, []);

  const dismissCompletedProfileUpdate = async (r) => {
    setCompletedProfileUpdates((prev) => prev.filter((x) => x.id !== r.id));
    try {
      await API.post(`/admin/profile-update-requests/${r.id}/ack`);
    } catch (_) { /* best-effort */ }
  };

  useEffect(() => {
    let active = true;
    API.get('/admin/new-client-requests')
      .then((res) => { if (active && Array.isArray(res.data)) setNewClientRequests(res.data); })
      .catch(() => { if (active) setNewClientRequestsFailed(true); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    API.get('/acuity/conflicts')
      .then((res) => { if (active && res.data) setAdminConflicts(res.data.conflicts || []); })
      .catch(() => { /* non-fatal */ });
    return () => { active = false; };
  }, []);

  const clearAdminConflict = async (conflict, mode) => {
    // always_allow keys on the entity (client/pairing); dismiss_once and
    // defer_to_therapist both key on this specific conflict's signature.
    const matchKey = mode === 'always_allow' ? conflict.entityKey : conflict.signature;
    setAdminConflicts((prev) => prev.filter((c) => {
      if (mode === 'always_allow') return !(c.kind === conflict.kind && c.entityKey === conflict.entityKey);
      return c.signature !== conflict.signature;
    }));
    try {
      await API.post('/acuity/conflicts/clear', { mode, conflictKind: conflict.kind, matchKey });
    } catch (_) { /* best-effort */ }
  };

  const fetchMyProfile = () => {
    API.get('/therapist/profile').then((r) => setMyProfile(r.data)).catch(() => {});
  };
  useEffect(() => { fetchMyProfile(); }, []);

  // Fetch stats and profiles on mount
  useEffect(() => {
    fetchTherapistsStats();
    fetchTherapistProfiles();
    fetchScheduleSummaries();
  }, []);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const u = JSON.parse(userStr);
      setCurrentUserId(u.id || null);
    }
  }, []);

  const fetchTherapistsStats = async () => {
    try {
      const res = await API.get('/admin/therapists-stats');
      const dataCleaned = res.data.map((t) => ({
        ...t,
        recent7: typeof t.recent7 === 'number' ? t.recent7 : 0,
        prev7: typeof t.prev7 === 'number' ? t.prev7 : 0,
      }));
      setTherapists(dataCleaned);
    } catch (err) {
      console.error('Error fetching therapists stats:', err);
      alert('Error fetching therapists stats');
    }
  };

  const fetchTherapistProfiles = async () => {
    try {
      const res = await API.get('/admin/therapist-profiles');
      const profilesMap = {};
      res.data.forEach((profile) => {
        profilesMap[profile.therapist_id] = profile;
      });
      setTherapistProfilesMap(profilesMap);
      setTeamMembers(res.data);
    } catch (err) {
      console.error('Error fetching therapist profiles:', err);
    }
  };

  const fetchScheduleSummaries = async () => {
    try {
      const res = await API.get('/acuity/all-summaries');
      const map = {};
      res.data.forEach((s) => {
        map[s.therapistUserId] = s;
      });
      setScheduleSummaries(map);
    } catch (err) {
      console.error('Error fetching schedule summaries:', err);
    }
  };

  // --------------- Celebration helpers ---------------
  const yearsSince = (dateStr, onDate = new Date()) => {
    if (!dateStr) return null;
    const dateOnly = String(dateStr).split('T')[0].split('-').map(Number);
    if (dateOnly.length < 3 || dateOnly.some((n) => Number.isNaN(n))) return null;
    const d = new Date(dateOnly[0], dateOnly[1] - 1, dateOnly[2]);
    if (isNaN(d)) return null;
    let years = onDate.getFullYear() - d.getFullYear();
    const hadAnniv =
      onDate.getMonth() > d.getMonth() ||
      (onDate.getMonth() === d.getMonth() && onDate.getDate() >= d.getDate());
    if (!hadAnniv) years -= 1;
    return Math.max(years, 0);
  };
  const startOfWeek = (d = new Date()) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = (day === 0 ? -6 : 1) - day;
    date.setDate(date.getDate() + diff);
    date.setHours(0, 0, 0, 0);
    return date;
  };
  const endOfWeek = (d = new Date()) => {
    const s = startOfWeek(d);
    const e = new Date(s);
    e.setDate(e.getDate() + 6);
    e.setHours(23, 59, 59, 999);
    return e;
  };
  const sameMonthDayThisYear = (dateStr) => {
    if (!dateStr) return null;
    const dateOnly = String(dateStr).split('T')[0].split('-').map(Number);
    if (dateOnly.length < 3 || dateOnly.some((n) => Number.isNaN(n))) return null;
    const d = new Date(dateOnly[0], dateOnly[1] - 1, dateOnly[2]);
    if (isNaN(d)) return null;
    const now = new Date();
    let candidate = new Date(now.getFullYear(), d.getMonth(), d.getDate());
    if (d.getMonth() === 1 && d.getDate() === 29 && candidate.getMonth() !== 1) {
      candidate = new Date(now.getFullYear(), 1, 28);
    }
    return candidate;
  };
  const inWeek = (dateObj, weekStart, weekEnd) => {
    if (!dateObj) return false;
    return dateObj >= weekStart && dateObj <= weekEnd;
  };
  const celebrationItems = useMemo(() => {
    const s = startOfWeek();
    const e = endOfWeek();
    return (teamMembers || []).flatMap((m) => {
      const items = [];
      const fullName = `${m.first_name} ${m.last_name}`.trim();
      const bd = sameMonthDayThisYear(m?.dob);
      if (inWeek(bd, s, e)) {
        items.push({ type: 'birthday', date: bd, name: fullName, years: null, photo: m.photo_url || null, userId: m.therapist_id, email: m.email });
      }
      const hd = sameMonthDayThisYear(m?.hire_date);
      if (inWeek(hd, s, e)) {
        items.push({
          type: 'anniversary',
          date: hd,
          name: fullName,
          years: yearsSince(m?.hire_date, hd),
          photo: m.photo_url || null,
          userId: m.therapist_id,
          email: m.email,
        });
      }
      return items;
    });
  }, [teamMembers]);

  const REQUEST_STATUS_CHIP = {
    pending: { label: 'Pending', bg: 'var(--steel-blue)' },
    accepted: { label: 'Accepted', bg: 'var(--success-color)' },
    rejected: { label: 'Rejected', bg: 'var(--error-color)' },
    info_admin: { label: 'Office follow-up', bg: 'var(--accent-color)' },
    info_self: { label: 'Therapist reaching out', bg: 'var(--accent-color)' },
  };
  const handleViewProfile = (therapistId) => {
    const profile = therapistProfilesMap[therapistId];
    if (profile) {
      setSelectedProfile(profile);
      setFormData({
        photoUrl: profile.photo_url || '',
        licenseNumber: profile.license_number || '',
        licenseType: profile.license_type || '',
        education: profile.education || '',
        clientDemographics: profile.client_demographics || '',
        acceptingClients: profile.accepting_clients !== undefined ? profile.accepting_clients : true,
        unavailableReason: profile.unavailable_reason || '',
        bio: profile.bio || '',
        interests: profile.interests || '',
        specialties: profile.specialties || '',
        yearsExperience: profile.years_experience || '',
        phone: profile.phone || '',
        phoneExtension: profile.phone_extension || '',
        dob: profile.dob ? String(profile.dob).substring(0, 10) : '',
        hireDate: profile.hire_date ? String(profile.hire_date).substring(0, 10) : '',
        emergencyContactName: profile.emergency_contact_name || '',
        emergencyContactPhone: profile.emergency_contact_phone || '',
      });
      setIsEditing(false);
      setShowProfileModal(true);
    } else {
      const therapist = therapists.find((t) => t.therapistId === therapistId);
      setSelectedProfile({
        therapist_id: therapistId,
        first_name: therapist?.firstName || '',
        last_name: therapist?.lastName || '',
      });
      setFormData({
        photoUrl: '',
        licenseNumber: '',
        licenseType: '',
        education: '',
        clientDemographics: '',
        acceptingClients: true,
        unavailableReason: '',
        bio: '',
        interests: '',
        specialties: '',
        yearsExperience: '',
        phone: '',
        phoneExtension: '',
        dob: '',
        hireDate: '',
        emergencyContactName: '',
        emergencyContactPhone: '',
      });
      setIsEditing(true);
      setShowProfileModal(true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveError('');
    setSaveSuccess(false);
    try {
      const therapistId = selectedProfile.therapist_id;
      const userPayload = {
        phone: formData.phone || null,
        phoneExtension: formData.phoneExtension || null,
        dob: formData.dob || null,
        hireDate: formData.hireDate || null,
        emergencyContactName: formData.emergencyContactName || null,
        emergencyContactPhone: formData.emergencyContactPhone || null,
      };
      const profilePayload = {
        photoUrl: formData.photoUrl || null,
        licenseNumber: formData.licenseNumber || null,
        licenseType: formData.licenseType || null,
        education: formData.education || null,
        clientDemographics: formData.clientDemographics || null,
        acceptingClients: !!formData.acceptingClients,
        unavailableReason: formData.unavailableReason || null,
        bio: formData.bio || null,
        interests: formData.interests || null,
        specialties: formData.specialties || null,
        yearsExperience: formData.yearsExperience || null,
      };
      await API.put(`/admin/users/${therapistId}`, userPayload);
      await API.put(`/admin/therapist-profiles/${therapistId}`, profilePayload);
      setSaveSuccess(true);
      await fetchTherapistProfiles();
      setTimeout(() => {
        setIsEditing(false);
        setSaveSuccess(false);
      }, 1500);
    } catch (error) {
      console.error('Error saving profile:', error);
      setSaveError('Error saving profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const renderers = {
    quickActions: () => <QuickActionsBar />,
    team: () => <TeamCard teamMembers={teamMembers} embedded />,
    myProfile: () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 120 }}>
        {myProfile ? (
          <>
            <div style={{ fontWeight: 700, fontSize: 16 }}>
              {timeGreeting()}, {myProfile.first_name}!
            </div>
            <div style={{ fontSize: 14 }}>{myProfile.first_name} {myProfile.last_name}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {myProfile.job_title || 'Add a job title so the team knows your role'}
            </div>
            <button
              onClick={() => setEditProfileOpen(true)}
              style={{ marginTop: 'auto', background: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start' }}
            >
              Edit My Profile
            </button>
          </>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>
        )}
      </div>
    ),
    newClientRequests: () => (
      <div>
        {newClientRequestsFailed ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>Couldn't load requests — try reloading the page.</div>
        ) : newClientRequests.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>No new-client requests in the last 30 days.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 320, overflowY: 'auto' }}>
            {newClientRequests.map((r) => {
              const chip = REQUEST_STATUS_CHIP[r.status] || REQUEST_STATUS_CHIP.pending;
              return (
                <div
                  key={r.acuityId}
                  onClick={() => setRequestDetailId(r.acuityId)}
                  title="Click for contact info and intake details"
                  style={{ border: '1px solid var(--border-color)', borderRadius: 10, padding: '10px 12px', fontSize: 14, cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700 }}>{r.clientName || 'Unknown client'}</span>
                    <span style={{ background: chip.bg, color: '#fff', borderRadius: 999, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                      {chip.label}
                    </span>
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>
                    {r.therapistName}
                    {r.apptDatetime && ` · ${new Date(r.apptDatetime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`}
                  </div>
                  {r.note && (
                    <div style={{ marginTop: 6, fontSize: 13, background: 'var(--surface-muted)', borderRadius: 6, padding: '6px 8px', whiteSpace: 'pre-wrap' }}>
                      {r.note}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div style={{ marginTop: 8, textAlign: 'right' }}>
          <Link to="/admin/new-client-requests" style={{ fontSize: 12, fontWeight: 600, color: 'var(--primary-color)' }}>
            View all →
          </Link>
        </div>
      </div>
    ),
    calendarManager: () => {
      const options = Object.values(scheduleSummaries)
        .filter((s) => s.calendarId)
        .map((s) => {
          const p = therapistProfilesMap[s.therapistUserId];
          const t = therapists.find((x) => x.therapistId === s.therapistUserId);
          const name = p ? `${p.first_name} ${p.last_name}` : t ? `${t.firstName} ${t.lastName}` : `Calendar ${s.calendarId}`;
          return { calendarId: s.calendarId, name };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
      return (
        <div>
          <select
            value={managedCalendarId}
            onChange={(e) => setManagedCalendarId(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-color)', fontSize: 13, marginBottom: 10 }}
          >
            <option value="">Choose a therapist's calendar…</option>
            {options.map((o) => (
              <option key={o.calendarId} value={o.calendarId}>{o.name}</option>
            ))}
          </select>
          {managedCalendarId ? (
            <ScheduleCard calendarId={managedCalendarId} actions embedded />
          ) : (
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              Pick a calendar to view, book, reschedule, or cancel appointments.
            </div>
          )}
        </div>
      );
    },
    practiceOverview: () => (
      <div style={summaryCardsContainerStyle}>
        <SessionSummaryCard />
        {!limited && <RevenueSummaryCard />}
      </div>
    ),
    // Week-by-week scheduled vs collected per therapist, attention first.
    trends: () => (
      <TherapistPerformanceCard profiles={therapistProfilesMap} onOpenProfile={handleViewProfile} />
    ),
    exports: () => <ExcelExportCard embedded />,
    canvas: () => <CommunityCanvas embedded />,
  };

  return (
    <div style={{ margin: '2rem' }}>
      <h2>Admin Dashboard</h2>
      <AnnouncementBanner audience="admin" />
      <NewClientRequestBanner requests={newClientBanner} onDismiss={dismissNewClientRequest} />
      <ProfileUpdateCompletedBanner requests={completedProfileUpdates} onDismiss={dismissCompletedProfileUpdate} />
      <CollectionBanner collections={newCollections} onDismiss={dismissCollection} />
      {adminConflicts.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 10px', color: '#7A2E1D' }}>
            Schedule conflicts ({adminConflicts.length})
          </h3>
          <ConflictBanner conflicts={adminConflicts} onClear={clearAdminConflict} showDefer />
        </div>
      )}
      {editProfileOpen && myProfile && (
        <ProfileEditModal profile={myProfile} onClose={() => { setEditProfileOpen(false); fetchMyProfile(); }} />
      )}
      {/* Celebration card when there are items */}
      {celebrationItems.length > 0 && (
        <CelebrationCard
          items={celebrationItems}
          currentUserId={currentUserId}
          onDrawCard={(item) => setCardTarget(item)}
        />
      )}
      <DashboardGrid
        widgets={visibleWidgets}
        renderers={renderers}
        layout={layout}
        onMove={move}
        onHide={hide}
        onShow={show}
        onReset={reset}
      />
      {/* Profile modal — lives at page level so it can overlay the grid */}
      {showProfileModal && selectedProfile && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <div style={modalHeaderStyle}>
              <h2 style={modalTitleStyle}>
                {selectedProfile.first_name} {selectedProfile.last_name}'s Profile
              </h2>
              <button
                onClick={() => {
                  setShowProfileModal(false);
                  setIsEditing(false);
                }}
                style={modalCloseButtonStyle}
              >
                &times;
              </button>
            </div>
            <div style={modalBodyStyle}>
              {isEditing ? (
                <form onSubmit={handleSaveProfile}>
                  <div style={formGridStyle}>
                    <div style={formColumnStyle}>
                      <div style={formGroupStyle}>
                        <label style={labelStyle}>Date of Birth</label>
                        <input
                          type="date"
                          name="dob"
                          value={formData.dob || ''}
                          onChange={handleInputChange}
                          style={inputStyle}
                        />
                      </div>
                      <div style={formGroupStyle}>
                        <label style={labelStyle}>Hire Date</label>
                        <input
                          type="date"
                          name="hireDate"
                          value={formData.hireDate || ''}
                          onChange={handleInputChange}
                          style={inputStyle}
                        />
                      </div>
                      <div style={formGroupStyle}>
                        <label style={labelStyle}>Phone</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone || ''}
                          onChange={handleInputChange}
                          style={inputStyle}
                          placeholder="e.g., (555) 123-4567"
                        />
                      </div>
                      <div style={formGroupStyle}>
                        <label style={labelStyle}>Extension (optional)</label>
                        <input
                          type="text"
                          name="phoneExtension"
                          value={formData.phoneExtension || ''}
                          onChange={handleInputChange}
                          style={inputStyle}
                          placeholder="e.g., 123"
                        />
                      </div>
                      <div style={formGroupStyle}>
                        <label style={labelStyle}>Emergency Contact Name</label>
                        <input
                          type="text"
                          name="emergencyContactName"
                          value={formData.emergencyContactName || ''}
                          onChange={handleInputChange}
                          style={inputStyle}
                          placeholder="e.g., Jane Doe"
                        />
                      </div>
                      <div style={formGroupStyle}>
                        <label style={labelStyle}>Emergency Contact Phone</label>
                        <input
                          type="tel"
                          name="emergencyContactPhone"
                          value={formData.emergencyContactPhone || ''}
                          onChange={handleInputChange}
                          style={inputStyle}
                          placeholder="e.g., 555-555-5555"
                        />
                      </div>
                      <div style={formGroupStyle}>
                        <label style={labelStyle}>Profile Photo URL</label>
                        <input
                          type="text"
                          name="photoUrl"
                          value={formData.photoUrl}
                          onChange={handleInputChange}
                          style={inputStyle}
                          placeholder="https://example.com/photo.jpg"
                        />
                      </div>
                      <div style={formGroupStyle}>
                        <label style={labelStyle}>License Number</label>
                        <input
                          type="text"
                          name="licenseNumber"
                          value={formData.licenseNumber}
                          onChange={handleInputChange}
                          style={inputStyle}
                          placeholder="e.g., 12345-A"
                        />
                      </div>
                      <div style={formGroupStyle}>
                        <label style={labelStyle}>License Type</label>
                        <select
                          name="licenseType"
                          value={formData.licenseType}
                          onChange={handleInputChange}
                          style={inputStyle}
                        >
                          <option value="">Select License Type</option>
                          <option value="LCSW">LCSW</option>
                          <option value="LPC">LPC</option>
                          <option value="LMFT">LMFT</option>
                          <option value="LMHC">LMHC</option>
                          <option value="PhD">PhD</option>
                          <option value="PsyD">PsyD</option>
                          <option value="MD">MD</option>
                        </select>
                      </div>
                      <div style={formGroupStyle}>
                        <label style={labelStyle}>Years of Experience</label>
                        <input
                          type="number"
                          name="yearsExperience"
                          value={formData.yearsExperience}
                          onChange={handleInputChange}
                          style={inputStyle}
                          min="0"
                        />
                      </div>
                      <div style={formGroupStyle}>
                        <label style={labelStyle}>Education</label>
                        <textarea
                          name="education"
                          value={formData.education}
                          onChange={handleInputChange}
                          style={{ ...inputStyle, minHeight: '80px' }}
                          placeholder="e.g., MSW from University of State, 2015"
                        />
                      </div>
                    </div>
                    <div style={formColumnStyle}>
                      <div style={formGroupStyle}>
                        <label style={labelStyle}>Specialties</label>
                        <textarea
                          name="specialties"
                          value={formData.specialties}
                          onChange={handleInputChange}
                          style={{ ...inputStyle, minHeight: '80px' }}
                          placeholder="e.g., Anxiety, Depression, PTSD"
                        />
                      </div>
                      <div style={formGroupStyle}>
                        <label style={labelStyle}>Client Demographics</label>
                        <textarea
                          name="clientDemographics"
                          value={formData.clientDemographics}
                          onChange={handleInputChange}
                          style={{ ...inputStyle, minHeight: '80px' }}
                          placeholder="e.g., Adults 18+, Teenagers, Couples"
                        />
                      </div>
                      <div style={formGroupStyle}>
                        <label style={labelStyle}>Bio</label>
                        <textarea
                          name="bio"
                          value={formData.bio}
                          onChange={handleInputChange}
                          style={{ ...inputStyle, minHeight: '120px' }}
                          placeholder="A brief bio about the therapist and their approach..."
                        />
                      </div>
                      <div style={formGroupStyle}>
                        <label style={labelStyle}>Personal Interests</label>
                        <textarea
                          name="interests"
                          value={formData.interests}
                          onChange={handleInputChange}
                          style={{ ...inputStyle, minHeight: '80px' }}
                          placeholder="e.g., Hiking, Cooking, Reading"
                        />
                      </div>
                    </div>
                  </div>
                  <div style={formGroupStyle}>
                    <div style={checkboxContainerStyle}>
                      <input
                        type="checkbox"
                        name="acceptingClients"
                        checked={formData.acceptingClients}
                        onChange={handleInputChange}
                        id="acceptingClients"
                      />
                      <label htmlFor="acceptingClients" style={{ marginLeft: '8px' }}>
                        Currently accepting new clients
                      </label>
                    </div>
                  </div>
                  {!formData.acceptingClients && (
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>Reason not accepting new clients</label>
                      <textarea
                        name="unavailableReason"
                        value={formData.unavailableReason}
                        onChange={handleInputChange}
                        style={{ ...inputStyle, minHeight: '80px' }}
                        placeholder="Please explain why they're not accepting new clients..."
                      />
                    </div>
                  )}
                  {saveError && <div style={errorMessageStyle}>{saveError}</div>}
                  {saveSuccess && <div style={successMessageStyle}>Profile saved successfully!</div>}
                  <div style={buttonContainerStyle}>
                    <button
                      type="button"
                      onClick={() => setIsEditing(false)}
                      style={{ ...buttonStyle, backgroundColor: 'var(--text-muted)' }}
                      disabled={isSaving}
                    >
                      Cancel
                    </button>
                    <button type="submit" style={buttonStyle} disabled={isSaving}>
                      {isSaving ? 'Saving...' : 'Save Profile'}
                    </button>
                  </div>
                </form>
              ) : (
                <div>
                  <div style={profileViewStyle}>
                    <div style={profileViewHeaderStyle}>
                      <div style={largePhotoContainerStyle}>
                        {formData.photoUrl ? (
                          <img
                            src={formData.photoUrl}
                            alt={`${selectedProfile.first_name} ${selectedProfile.last_name}`}
                            style={largePhotoStyle}
                          />
                        ) : (
                          <div
                            style={{
                              ...largePhotoStyle,
                              backgroundColor: 'var(--border-color)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '2.5rem',
                              color: 'var(--text-muted)',
                            }}
                          >
                            {selectedProfile.first_name ? selectedProfile.first_name.charAt(0) : ''}
                            {selectedProfile.last_name ? selectedProfile.last_name.charAt(0) : ''}
                          </div>
                        )}
                      </div>
                      <div style={profileViewInfoStyle}>
                        <h3 style={profileViewNameStyle}>
                          {selectedProfile.first_name} {selectedProfile.last_name}
                          {formData.licenseType && (
                            <span style={profileViewLicenseStyle}>{formData.licenseType}</span>
                          )}
                        </h3>
                        <div style={profileViewStatusStyle}>
                          <div
                            style={{
                              width: '12px',
                              height: '12px',
                              borderRadius: '50%',
                              backgroundColor: formData.acceptingClients ? 'var(--success-color)' : '#F44336',
                              marginRight: '10px',
                            }}
                          ></div>
                          <span style={{ fontWeight: 'bold' }}>
                            {formData.acceptingClients ? 'Accepting new clients' : 'Not accepting new clients'}
                          </span>
                        </div>
                        {!formData.acceptingClients && formData.unavailableReason && (
                          <div style={unavailableReasonBoxStyle}>
                            <p>
                              <strong>Reason:</strong> {formData.unavailableReason}
                            </p>
                          </div>
                        )}
                        {formData.licenseNumber && (
                          <p style={infoItemStyle}>
                            <strong>License #:</strong> {formData.licenseNumber}
                          </p>
                        )}
                        {formData.yearsExperience && (
                          <p style={infoItemStyle}>
                            <strong>Experience:</strong> {formData.yearsExperience} years
                          </p>
                        )}
                        {formData.dob && (
                          <p style={infoItemStyle}>
                            <strong>DOB:</strong> {formData.dob}
                          </p>
                        )}
                        {formData.hireDate && (
                          <p style={infoItemStyle}>
                            <strong>Hire Date:</strong> {formData.hireDate}
                          </p>
                        )}
                        {formData.phone && (
                          <p style={infoItemStyle}>
                            <strong>Phone:</strong> {formData.phone}
                            {formData.phoneExtension && ` ext. ${formData.phoneExtension}`}
                          </p>
                        )}
                        {(formData.emergencyContactName || formData.emergencyContactPhone) && (
                          <p style={infoItemStyle}>
                            <strong>Emergency Contact:</strong> {formData.emergencyContactName || '—'}
                            {formData.emergencyContactPhone ? ` (${formData.emergencyContactPhone})` : ''}
                          </p>
                        )}
                      </div>
                    </div>
                    <div style={profileSectionsStyle}>
                      <div style={sectionContainerStyle}>
                        <h4 style={sectionHeadingStyle}>Education</h4>
                        <p style={sectionContentStyle}>{formData.education || 'No education information provided.'}</p>
                      </div>
                      <div style={sectionContainerStyle}>
                        <h4 style={sectionHeadingStyle}>Specialties</h4>
                        <p style={sectionContentStyle}>{formData.specialties || 'No specialties listed.'}</p>
                      </div>
                      <div style={sectionContainerStyle}>
                        <h4 style={sectionHeadingStyle}>Client Demographics</h4>
                        <p style={sectionContentStyle}>{formData.clientDemographics || 'No client demographics specified.'}</p>
                      </div>
                      <div style={sectionContainerStyle}>
                        <h4 style={sectionHeadingStyle}>Bio</h4>
                        <p style={sectionContentStyle}>{formData.bio || 'No bio available.'}</p>
                      </div>
                      <div style={sectionContainerStyle}>
                        <h4 style={sectionHeadingStyle}>Personal Interests</h4>
                        <p style={sectionContentStyle}>{formData.interests || 'No personal interests listed.'}</p>
                      </div>
                    </div>
                  </div>
                  <div style={editButtonContainerStyle}>
                    <button onClick={() => setIsEditing(true)} style={editButtonStyle}>
                      Edit Profile
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {cardTarget && (
        <BirthdayCardModal
          recipientId={cardTarget.userId}
          recipientFirstName={cardTarget.name.split(' ')[0]}
          type={cardTarget.type}
          onClose={() => setCardTarget(null)}
        />
      )}
      {requestDetailId && (
        <NewClientDetailModal acuityId={requestDetailId} onClose={() => setRequestDetailId(null)} />
      )}
    </div>
  );
}

/* ------------------------------- Styles ------------------------------- */
const qaWrap = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: '12px',
  marginBottom: '24px',
};
const qaCard = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '14px 16px',
  background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
  border: '1px solid var(--border-color)',
  borderRadius: '14px',
  textDecoration: 'none',
  color: '#0f172a',
  boxShadow: '0 6px 16px rgba(0,0,0,0.06)',
  transition: 'transform .12s ease, box-shadow .12s ease, border-color .12s ease',
};
const qaIcon = { fontSize: '1.4rem', lineHeight: 1 };
const qaLabel = { fontWeight: 700 };

const summaryCardsContainerStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '1.5rem',
  marginBottom: '2rem',
};
const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 };
const modalContentStyle = { backgroundColor: 'white', borderRadius: '8px', width: '90%', maxWidth: '900px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)' };
const modalHeaderStyle = { padding: '1.5rem', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const modalTitleStyle = { margin: 0, fontSize: '1.5rem' };
const modalCloseButtonStyle = { background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--text-muted)' };
const modalBodyStyle = { padding: '1.5rem' };
const formGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' };
const formColumnStyle = { display: 'flex', flexDirection: 'column' };
const formGroupStyle = { marginBottom: '1.5rem' };
const labelStyle = { display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' };
const inputStyle = { width: '100%', padding: '0.75rem', borderRadius: '4px', border: '1px solid var(--border-color)', fontSize: '1rem' };
const checkboxContainerStyle = { display: 'flex', alignItems: 'center' };
const buttonContainerStyle = { display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' };
const buttonStyle = { padding: '0.75rem 1.5rem', backgroundColor: 'var(--success-color)', color: 'white', border: 'none', borderRadius: '4px', fontSize: '1rem', cursor: 'pointer', transition: 'background-color 0.2s' };
const errorMessageStyle = { backgroundColor: '#f8d7da', color: '#721c24', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem' };
const successMessageStyle = { backgroundColor: '#d4edda', color: '#155724', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem' };
const profileViewStyle = { display: 'flex', flexDirection: 'column', gap: '2rem' };
const profileViewHeaderStyle = { display: 'flex', gap: '2rem', alignItems: 'flex-start' };
const largePhotoContainerStyle = { flexShrink: 0 };
const largePhotoStyle = { width: '150px', height: '150px', borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--success-color)' };
const profileViewInfoStyle = { flex: 1 };
const profileViewNameStyle = { fontSize: '1.8rem', marginTop: 0, marginBottom: '1rem', display: 'flex', alignItems: 'center' };
const profileViewLicenseStyle = { fontSize: '1rem', backgroundColor: 'var(--border-color)', color: 'var(--text-strong)', padding: '0.2rem 0.6rem', borderRadius: '4px', marginLeft: '1rem', fontWeight: 'normal' };
const profileViewStatusStyle = { display: 'flex', alignItems: 'center', marginBottom: '1rem' };
const unavailableReasonBoxStyle = { backgroundColor: '#fff3cd', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem' };
const infoItemStyle = { margin: '0.5rem 0' };
const profileSectionsStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '1.5rem' };
const sectionContainerStyle = { marginBottom: '1.5rem' };
const sectionHeadingStyle = { borderBottom: '2px solid var(--success-color)', paddingBottom: '0.5rem', marginBottom: '0.5rem' };
const sectionContentStyle = { lineHeight: '1.6' };
const editButtonContainerStyle = { display: 'flex', justifyContent: 'center', marginTop: '2rem' };
const editButtonStyle = { backgroundColor: 'var(--success-color)', color: 'white', border: 'none', padding: '0.75rem 2rem', borderRadius: '4px', fontSize: '1rem', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.2)', transition: 'background-color 0.2s, transform 0.1s' };

/* Excel card style constants for ExcelExportCard */
const excelCardStyle = { flex: '1 1 400px', minWidth: '350px', maxWidth: '500px', padding: '1.5rem', backgroundColor: '#fff8e1', borderRadius: '8px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)', transition: 'transform 0.2s ease, box-shadow 0.2s ease' };
const excelCardHeaderStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' };
const excelCardTitleStyle = { margin: 0, fontSize: '1.3rem', color: 'var(--primary-color)' };
const excelIconStyle = { fontSize: '1.5rem' };
const excelCardBodyStyle = { display: 'flex', flexDirection: 'column', gap: '1rem' };
const excelDescriptionStyle = { fontSize: '0.9rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.4 };
const quickButtonsStyle = { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' };
const quickButtonStyle = { padding: '0.4rem 0.8rem', backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer', transition: 'background-color 0.2s' };
const dateRangeStyle = { display: 'flex', gap: '1rem', alignItems: 'end' };
const dateInputGroupStyle = { display: 'flex', flexDirection: 'column', flex: 1 };
const excelLabelStyle = { fontSize: '0.85rem', color: 'var(--primary-color)', marginBottom: '0.25rem', fontWeight: '500' };
const dateInputStyle = { padding: '0.5rem', border: '1px solid var(--border-color)', borderRadius: '4px', fontSize: '0.9rem' };
const exportButtonStyle = { padding: '0.75rem 1rem', backgroundColor: '#e67e22', color: 'white', border: 'none', borderRadius: '6px', fontSize: '1rem', fontWeight: 'bold', transition: 'background-color 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' };
const loadingTextStyle = { display: 'flex', alignItems: 'center', gap: '0.5rem' };
const spinnerStyle = { animation: 'spin 1s linear infinite' };
const excelSuccessMessageStyle = { backgroundColor: '#d4edda', color: '#155724', padding: '0.75rem', borderRadius: '4px', fontSize: '0.9rem', border: '1px solid #c3e6cb' };
const excelErrorMessageStyle = { backgroundColor: '#f8d7da', color: '#721c24', padding: '0.75rem', borderRadius: '4px', fontSize: '0.9rem', border: '1px solid #f5c6cb' };
const infoBoxStyle = { backgroundColor: '#e8f4f8', padding: '1rem', borderRadius: '6px', fontSize: '0.85rem', border: '1px solid #bee5eb' };
const infoHeaderStyle = { fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--primary-color)' };
const infoListStyle = { margin: 0, paddingLeft: '1.2rem', color: '#34495e' };

// Excel export component
const ExcelExportCard = ({ embedded = false }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exportError, setExportError] = useState('');
  const [exportSuccess, setExportSuccess] = useState(false);
  useEffect(() => {
    const today = todayEastern();
    const past = new Date();
    past.setDate(past.getDate() - 30);
    past.setMinutes(past.getMinutes() - past.getTimezoneOffset());
    setEndDate(today);
    setStartDate(past.toISOString().split('T')[0]);
  }, []);
  const handleExport = async () => {
    setIsExporting(true);
    setExportError('');
    setExportSuccess(false);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const response = await fetch(`${API.defaults.baseURL}/admin/export-therapist-daily-summary?${params}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) throw new Error('Export failed');
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'therapist_daily_summary.xlsx';
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="(.+)"/);
        if (matches) filename = matches[1];
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 3000);
    } catch (e) {
      console.error('Export error:', e);
      setExportError('Failed to export data. Please try again.');
      setTimeout(() => setExportError(''), 5000);
    } finally {
      setIsExporting(false);
    }
  };
  const handleQuickExport = (days) => {
    const pastDate = new Date();
    pastDate.setDate(pastDate.getDate() - days);
    pastDate.setMinutes(pastDate.getMinutes() - pastDate.getTimezoneOffset());
    setStartDate(pastDate.toISOString().split('T')[0]);
    setEndDate(todayEastern());
  };
  return (
    <div style={excelCardStyle}>
      {!embedded && (
        <div style={excelCardHeaderStyle}>
          <h3 style={excelCardTitleStyle}>Export Therapist Summary</h3>
          <div style={excelIconStyle}>📊</div>
        </div>
      )}
      <div style={excelCardBodyStyle}>
        <p style={excelDescriptionStyle}>Download an Excel file with daily session and billing summaries for each therapist.</p>
        <div style={quickButtonsStyle}>
          <button style={quickButtonStyle} onClick={() => handleQuickExport(7)} disabled={isExporting}>Last 7 Days</button>
          <button style={quickButtonStyle} onClick={() => handleQuickExport(30)} disabled={isExporting}>Last 30 Days</button>
          <button style={quickButtonStyle} onClick={() => handleQuickExport(90)} disabled={isExporting}>Last 90 Days</button>
        </div>
        <div style={dateRangeStyle}>
          <div style={dateInputGroupStyle}>
            <label style={excelLabelStyle}>From:</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={dateInputStyle} disabled={isExporting} />
          </div>
          <div style={dateInputGroupStyle}>
            <label style={excelLabelStyle}>To:</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={dateInputStyle} disabled={isExporting} />
          </div>
        </div>
        <button onClick={handleExport} disabled={isExporting || !startDate || !endDate} style={{ ...exportButtonStyle, opacity: isExporting || !startDate || !endDate ? 0.6 : 1, cursor: isExporting || !startDate || !endDate ? 'not-allowed' : 'pointer' }}>
          {isExporting ? <span style={loadingTextStyle}><span style={spinnerStyle}>⏳</span> Generating Export...</span> : <span>📥 Export to Excel</span>}
        </button>
        {exportSuccess && <div style={excelSuccessMessageStyle}>✅ Export completed successfully! Check your downloads folder.</div>}
        {exportError && <div style={excelErrorMessageStyle}>❌ {exportError}</div>}
        <div style={infoBoxStyle}>
          <div style={infoHeaderStyle}>📋 Export includes:</div>
          <ul style={infoListStyle}>
            <li>Date and therapist name</li>
            <li>Number of sessions per day</li>
            <li>Total billed and collected amounts</li>
            <li>Outstanding balances</li>
            <li>Session status breakdown</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;